const features = [
  {
    icon: "💳",
    title: "Payments",
    href: "/api/payments",
    description:
      "Charge, refund, void, and capture credit cards. Support for bulk charges and pre-authorization.",
  },
  {
    icon: "🏦",
    title: "ACH Transfers",
    href: "/api/ach",
    description:
      "Process bank transfers via ACH. Supports charge, refund, and void operations.",
  },
  {
    icon: "👥",
    title: "Customer Vault",
    href: "/customer-vault",
    description:
      "Store and manage customer payment profiles securely. Support for multiple cards and bank accounts per customer.",
  },
  {
    icon: "🔁",
    title: "Subscriptions",
    href: "/api/subscriptions",
    description:
      "Create recurring billing plans and subscriptions with flexible billing cycles.",
  },
  {
    icon: "🧾",
    title: "Invoicing",
    href: "/api/invoices",
    description:
      "Generate, send, and manage invoices. Includes line items, taxes, and delivery via email.",
  },
  {
    icon: "🖥️",
    title: "Terminal",
    href: "/api/terminal",
    description:
      "Integrate physical payment terminals (BridgePay & Strictly) for in-person payments.",
  },
  {
    icon: "🔗",
    title: "Payment Links",
    href: "/api/payment-links",
    description:
      "Create shareable payment links for one-time or recurring payments without a checkout page.",
  },
  {
    icon: "🔔",
    title: "Webhooks",
    href: "/webhooks",
    description:
      "Receive real-time event notifications for payment status changes and other activity.",
  },
  {
    icon: "📊",
    title: "Reports",
    href: "/api/reports",
    description:
      "Access transaction and deposit reports to reconcile payments and monitor your business.",
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-24 px-6 text-center border-b">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm text-muted-foreground mb-8">
            <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />
            REST API · OpenAPI 3.0 · v1.0.0
          </div>
          <h1 className="text-5xl font-bold tracking-tight mb-5 leading-tight">
            The Strictly <br />
            Payment API
          </h1>
          <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            A complete REST API for payments, subscriptions, terminals, and
            invoicing. Integrate card, ACH, and recurring billing into your
            application in minutes.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <a
              href="/introduction"
              className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Read the Docs
            </a>
            <a
              href="/api"
              className="inline-flex items-center justify-center rounded-md border bg-background px-6 py-3 text-sm font-medium hover:bg-muted transition-colors"
            >
              API Reference →
            </a>
          </div>
        </div>
      </section>

      {/* Quick stats */}
      <section className="py-12 px-6 border-b bg-muted/30">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { label: "Endpoints", value: "60+" },
            { label: "Payment methods", value: "Card & ACH" },
            { label: "Base URL", value: "api.paywithzero.net" },
            { label: "Version", value: "v1" },
          ].map(({ label, value }) => (
            <div key={label}>
              <div className="text-2xl font-bold">{value}</div>
              <div className="text-sm text-muted-foreground mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features grid */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3">
            Everything you need
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto">
            From one-time charges to complex recurring billing — the Strictly
            API has you covered.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(({ icon, title, description, href }) => (
              <a
                key={title}
                href={href}
                className="block rounded-lg border p-6 hover:border-primary/50 hover:bg-muted/40 transition-colors"
              >
                <div className="text-2xl mb-3">{icon}</div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Quick start code snippet */}
      <section className="py-20 px-6 bg-muted/30 border-t border-b">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">
                Up and running in minutes
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Authenticate with HTTP Basic auth and your{" "}
                <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">
                  key-hash
                </code>{" "}
                header. Every request to{" "}
                <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">
                  api.paywithzero.net/v1
                </code>{" "}
                is secured with these two credentials.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="/getting-started"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  → Quick start guide
                </a>
                <a
                  href="/authentication"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  → Authentication docs
                </a>
                <a
                  href="/sandbox"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  → Try the sandbox
                </a>
              </div>
            </div>
            <div className="rounded-lg border bg-background overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-muted/50 text-xs text-muted-foreground font-mono">
                <span className="h-3 w-3 rounded-full bg-red-400" />
                <span className="h-3 w-3 rounded-full bg-yellow-400" />
                <span className="h-3 w-3 rounded-full bg-green-400" />
                <span className="ml-2">charge.sh</span>
              </div>
              <pre className="p-4 text-sm font-mono overflow-x-auto leading-relaxed">
                <code>{`curl -X POST \\
  https://api.paywithzero.net/v1/public/payment/charge \\
  -u "email:password" \\
  -H "key-hash: YOUR_KEY_HASH" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 1000,
    "card": {
      "paymentToken": "TOKEN_FROM_ZEROGATEWAY_JS",
      "number": "411111******1111"
    },
    "order": { "amount": 1000, "shipping": 0, "tax": 0, "discount": 0 },
    "capture": true
  }'`}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Ready to integrate?</h2>
          <p className="text-muted-foreground mb-8">
            Use the sandbox environment to test your integration before going
            live. No approval needed.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <a
              href="/sandbox"
              className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Try the Sandbox
            </a>
            <a
              href="/api"
              className="inline-flex items-center justify-center rounded-md border bg-background px-6 py-3 text-sm font-medium hover:bg-muted transition-colors"
            >
              Explore the API
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
